import { AdminPanelSettings, Delete, Edit, Person, PersonAdd, ToggleOff, ToggleOn, Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import axios from "axios";
import React, { useState } from "react";

import { ActionButton } from "../../components/ActionButton";
import { BaseDataGrid } from "../../components/BaseDataGrid";
import { authHeader, userData } from "../../helpers/auth";
import { convertToDate } from "../../helpers/dateFormat";
import endpoints from "../../helpers/endpoints";
import { toNameFormat } from "../../helpers/functions";
import { displayToast } from "../../helpers/toast";
import useAxios from "../../hooks/useAxios";
import { RolesType, UserType } from "../../types/User";

interface FormUserModalState {
  open: boolean;
  isEditing: boolean;
  userId?: number;
  name: string;
  lastName: string;
  email: string;
  role: RolesType;
  password: string;
  confirmPassword: string;
  isActive: boolean;
}

const INITIAL_FORM_STATE: FormUserModalState = {
  open: false,
  isEditing: false,
  name: "",
  lastName: "",
  email: "",
  role: "operator",
  password: "",
  confirmPassword: "",
  isActive: true,
};

export const Users = () => {
  const currentUser = userData();

  const {
    data: users,
    loading: loadingUsers,
    refetch: refetchUsers,
  } = useAxios<UserType[]>({
    url: endpoints.getUsers,
    initialData: [],
  });

  const [formState, setFormState] = useState<FormUserModalState>(INITIAL_FORM_STATE);
  const [formError, setFormError] = useState<string>("");
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [statusModalState, setStatusModalState] = useState<{
    open: boolean;
    user: UserType | null;
  }>({
    open: false,
    user: null,
  });
  const [loadingToggle, setLoadingToggle] = useState(false);

  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    user: UserType | null;
  }>({
    open: false,
    user: null,
  });
  const [loadingDelete, setLoadingDelete] = useState(false);

  const handleOpenCreate = () => {
    setFormError("");
    setShowPassword(false);
    setFormState({
      ...INITIAL_FORM_STATE,
      open: true,
      isEditing: false,
    });
  };

  const handleOpenEdit = (user: UserType) => {
    setFormError("");
    setShowPassword(false);
    setFormState({
      open: true,
      isEditing: true,
      userId: user.id,
      name: user.name || "",
      lastName: user.lastName || "",
      email: user.email || "",
      role: user.role || "operator",
      password: "",
      confirmPassword: "",
      isActive: user.isActive ?? true,
    });
  };

  const handleCloseForm = () => {
    setFormState(INITIAL_FORM_STATE);
    setFormError("");
    setShowPassword(false);
  };

  const handleConfirmToggleStatus = async () => {
    if (!statusModalState.user || loadingToggle) return;
    setLoadingToggle(true);
    try {
      const { headers } = authHeader("a/j");
      await axios.put(`${endpoints.toggleUserStatus}/${statusModalState.user.id}`, {}, { headers });
      displayToast({
        message: `Usuario ${statusModalState.user.isActive ? "desactivado" : "activado"} con éxito`,
        statusCode: 200,
      });
      setStatusModalState({ open: false, user: null });
      refetchUsers();
    } catch (error: any) {
      displayToast(error?.response?.data || error, error);
    } finally {
      setLoadingToggle(false);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!formState.name.trim() || !formState.lastName.trim() || !formState.email.trim()) {
      setFormError("Por favor completa todos los campos obligatorios.");
      return;
    }

    if (!formState.isEditing) {
      if (!formState.password || formState.password.length < 8) {
        setFormError("La contraseña debe tener al menos 8 caracteres.");
        return;
      }
      if (formState.password !== formState.confirmPassword) {
        setFormError("Las contraseñas no coinciden.");
        return;
      }
    } else {
      if (formState.password && formState.password.length < 8) {
        setFormError("La nueva contraseña debe tener al menos 8 caracteres.");
        return;
      }
      if (formState.password && formState.password !== formState.confirmPassword) {
        setFormError("Las contraseñas no coinciden.");
        return;
      }
    }

    setLoadingSubmit(true);
    try {
      const { headers } = authHeader("a/j");
      if (!formState.isEditing) {
        await axios.post(
          endpoints.newUser,
          {
            name: formState.name,
            lastName: formState.lastName,
            email: formState.email,
            role: formState.role,
            password: formState.password,
          },
          { headers },
        );
        displayToast({ message: "Usuario creado exitosamente", statusCode: 201 });
      } else {
        const payload: any = {
          name: formState.name,
          lastName: formState.lastName,
          email: formState.email,
          role: formState.role,
          isActive: formState.isActive,
        };
        if (formState.password && formState.password.trim().length >= 8) {
          payload.password = formState.password.trim();
        }
        await axios.put(`${endpoints.updateUser}/${formState.userId}`, payload, { headers });
        displayToast({ message: "Usuario actualizado exitosamente", statusCode: 200 });
      }
      handleCloseForm();
      refetchUsers();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || "Ocurrió un error al procesar la solicitud";
      setFormError(Array.isArray(errorMsg) ? errorMsg.join(", ") : errorMsg);
      displayToast(error?.response?.data || error, error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModalState.user || loadingDelete) return;
    setLoadingDelete(true);
    try {
      const { headers } = authHeader();
      const res = await axios.delete(`${endpoints.deleteUser}/${deleteModalState.user.id}`, { headers });
      const msg = res.data?.message || "Usuario eliminado correctamente";
      displayToast({ message: msg, statusCode: 200 });
      setDeleteModalState({ open: false, user: null });
      refetchUsers();
    } catch (error: any) {
      displayToast(error?.response?.data || error, error);
    } finally {
      setLoadingDelete(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      description: "Identificador de usuario",
      width: 60,
    },
    {
      field: "name",
      headerName: "Nombre y Apellido",
      description: "Nombre completo",
      minWidth: 160,
      flex: 25,
      renderCell: (params: { row: UserType }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          {params.row.role === "admin" ? <AdminPanelSettings fontSize="small" color="primary" /> : <Person fontSize="small" color="action" />}
          <span>{toNameFormat(`${params.row.lastName}, ${params.row.name}`)}</span>
        </Stack>
      ),
    },
    {
      field: "email",
      headerName: "Correo Electrónico",
      description: "Email de acceso",
      minWidth: 180,
      flex: 30,
    },
    {
      field: "role",
      headerName: "Rol",
      description: "Rol en el sistema",
      minWidth: 120,
      flex: 15,
      renderCell: (params: { row: UserType }) => (
        <Chip
          label={params.row.role === "admin" ? "Administrador" : "Operador"}
          color={params.row.role === "admin" ? "primary" : "default"}
          size="small"
          variant={params.row.role === "admin" ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "isActive",
      headerName: "Estado",
      description: "Estado de la cuenta",
      minWidth: 110,
      flex: 15,
      renderCell: (params: { row: UserType }) => (
        <Chip label={params.row.isActive ? "Activo" : "Inactivo"} color={params.row.isActive ? "success" : "error"} size="small" variant="outlined" />
      ),
    },
    {
      field: "applications_attended",
      headerName: "Atenciones",
      description: "Solicitudes y entradas atendidas",
      minWidth: 140,
      flex: 20,
      renderCell: (params: { row: UserType }) => {
        const solCount = params.row.applicationsCount ?? params.row.applications_attended?.length ?? 0;
        const entCount = params.row.rejectedsCount ?? params.row.rejecteds_attended?.length ?? 0;
        return (
          <Tooltip title={`${solCount} solicitudes / ${entCount} entradas atendidas`}>
            <Chip size="small" label={`${solCount} sol. / ${entCount} ent.`} variant="outlined" color={solCount + entCount > 0 ? "primary" : "default"} />
          </Tooltip>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Creado",
      description: "Fecha de registro",
      width: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (value) => new Date(value),
      renderCell: (params: { row: UserType }) => {
        if (!params.row.createdAt) return "-";
        const [date, hour] = convertToDate(params.row.createdAt);
        return (
          <Tooltip title={hour}>
            <span>{date}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      minWidth: 130,
      type: "actions",
      renderCell: (params: { row: UserType }) => {
        const isSelf = params.row.id === currentUser?.sub;
        return (
          <Stack direction="row" spacing={0.5}>
            <ActionButton title="Editar usuario" onClick={() => handleOpenEdit(params.row)} icon={<Edit fontSize="small" />} color="warning" size="small" />
            <Tooltip title={params.row.isActive ? "Desactivar usuario" : "Activar usuario"}>
              <span>
                <IconButton
                  size="small"
                  color={params.row.isActive ? "success" : "default"}
                  disabled={isSelf}
                  onClick={() => setStatusModalState({ open: true, user: params.row })}
                >
                  {params.row.isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isSelf ? "No puedes eliminarte a ti mismo" : "Eliminar usuario"}>
              <span>
                <IconButton size="small" color="error" disabled={isSelf} onClick={() => setDeleteModalState({ open: true, user: params.row })}>
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box width="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Gestión de Usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra los operadores y administradores del sistema, sus accesos y contraseñas.
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<PersonAdd />} onClick={handleOpenCreate}>
          Nuevo Usuario
        </Button>
      </Stack>

      <Paper sx={{ width: "100%" }} elevation={3}>
        <BaseDataGrid rows={users} columns={columns} loading={loadingUsers} />
      </Paper>

      {/* Modal Crear / Editar */}
      <Dialog open={formState.open} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {formState.isEditing ? <Edit color="primary" /> : <PersonAdd color="primary" />}
          {formState.isEditing ? `Editar Usuario: ${formState.name} ${formState.lastName}` : "Crear Nuevo Usuario"}
        </DialogTitle>
        <form onSubmit={handleSubmitForm}>
          <DialogContent dividers>
            {formError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {formError}
              </Typography>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre"
                  name="name"
                  value={formState.name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Apellido"
                  name="lastName"
                  value={formState.lastName}
                  onChange={(e) => setFormState((prev) => ({ ...prev, lastName: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={formState.email}
                  onChange={(e) => setFormState((prev) => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel id="role-select-label">Rol</InputLabel>
                  <Select
                    labelId="role-select-label"
                    label="Rol"
                    value={formState.role}
                    onChange={(e) => setFormState((prev) => ({ ...prev, role: e.target.value as RolesType }))}
                  >
                    <MenuItem value="operator">Operador</MenuItem>
                    <MenuItem value="admin">Administrador</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={formState.isEditing ? "Nueva Contraseña (opcional)" : "Contraseña"}
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formState.password}
                  onChange={(e) => setFormState((prev) => ({ ...prev, password: e.target.value }))}
                  fullWidth
                  required={!formState.isEditing}
                  helperText={formState.isEditing ? "Dejar en blanco para conservar la actual" : "Mínimo 8 caracteres"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={formState.isEditing ? "Confirmar Nueva Contraseña" : "Confirmar Contraseña"}
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formState.confirmPassword}
                  onChange={(e) => setFormState((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  fullWidth
                  required={!formState.isEditing && Boolean(formState.password)}
                  disabled={formState.isEditing && !formState.password}
                />
              </Grid>
              {formState.isEditing && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formState.isActive}
                        onChange={(e) => setFormState((prev) => ({ ...prev, isActive: e.target.checked }))}
                        color="success"
                      />
                    }
                    label={formState.isActive ? "Usuario Activo" : "Usuario Inactivo"}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseForm} color="inherit">
              Cancelar
            </Button>
            <LoadingButton type="submit" variant="contained" color="primary" loading={loadingSubmit}>
              {formState.isEditing ? "Guardar Cambios" : "Crear Usuario"}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Modal Confirmar Cambio de Estado (Activar / Desactivar) */}
      <Dialog open={statusModalState.open} onClose={() => !loadingToggle && setStatusModalState({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          {statusModalState.user?.isActive ? <ToggleOff color="warning" /> : <ToggleOn color="success" />}
          {statusModalState.user?.isActive ? "¿Desactivar usuario?" : "¿Activar usuario?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea {statusModalState.user?.isActive ? "desactivar" : "activar"} al usuario{" "}
            <strong>
              {statusModalState.user?.name} {statusModalState.user?.lastName}
            </strong>
            ?
          </DialogContentText>
          {statusModalState.user?.isActive && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              El usuario no podrá iniciar sesión ni registrar operaciones mientras esté inactivo.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setStatusModalState({ open: false, user: null })} color="inherit" disabled={loadingToggle}>
            Cancelar
          </Button>
          <LoadingButton
            onClick={handleConfirmToggleStatus}
            color={statusModalState.user?.isActive ? "warning" : "success"}
            variant="contained"
            loading={loadingToggle}
          >
            {statusModalState.user?.isActive ? "Desactivar" : "Activar"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmar Eliminación */}
      <Dialog open={deleteModalState.open} onClose={() => !loadingDelete && setDeleteModalState({ open: false, user: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Delete color="error" />
          ¿Eliminar usuario?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar al usuario{" "}
            <strong>
              {deleteModalState.user?.name} {deleteModalState.user?.lastName}
            </strong>{" "}
            ({deleteModalState.user?.email})?
          </DialogContentText>
          {((deleteModalState.user?.applicationsCount ?? deleteModalState.user?.applications_attended?.length ?? 0) > 0 ||
            (deleteModalState.user?.rejectedsCount ?? deleteModalState.user?.rejecteds_attended?.length ?? 0) > 0) && (
            <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 1.5 }}>
              Nota: Este usuario tiene trámites asociados. Se realizará un borrado lógico (desactivación) para preservar el historial.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteModalState({ open: false, user: null })} color="inherit" disabled={loadingDelete}>
            Cancelar
          </Button>
          <LoadingButton onClick={handleDelete} color="error" variant="contained" loading={loadingDelete}>
            Eliminar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
